import { useQuery, QueryClient } from "@tanstack/react-query";
import { GET_MY_USER_RECORDS } from "../api/queries";
import { useMemo } from "react";
import { graphQLClient } from "../graphqlClient";
import { useStores } from "./useStores";
import { hasAuthToken } from "../utils/tokenUtils";
import { FullUserRecord, ExtendedUser, UniqueIdentifier } from "@/types/user";

// Returns a full list of user records and loading state
export const useFullCurrentUser = () => {
  const { userStore } = useStores();
  const preferredUserPath = userStore?.getPreferredUserPath();

  const {
    data: userRecords,
    isLoading: userRecordsLoading,
    error: userRecordsError,
  } = useQuery<FullUserRecord[]>({
    queryKey: ["myUserRecords"],
    queryFn: async () => {
      const response = await graphQLClient.request<{
        getMyUserRecords: FullUserRecord[];
      }>(GET_MY_USER_RECORDS);
      return response.getMyUserRecords;
    },
    staleTime: 1000 * 10, // Consider data fresh for 10 seconds
    gcTime: 1000 * 60 * 60 * 12, // Keep unused data in cache for 12 hours
    // See useMyProducts: no credentials, nothing to fetch.
    enabled: hasAuthToken(),
  });

  const activeUserRecord = useMemo((): ExtendedUser | null => {
    if (userRecordsLoading) return null;
    if (!userRecords || userRecords.length === 0) return null;

    let userRecord: FullUserRecord;

    if (userRecords.length === 1) {
      userRecord = userRecords[0];
    } else {
      // Apply preferred path if it maps to a known record, else use the first.
      // post-login is responsible for gating on isResolving from usePostLoginResolver
      // before treating activeUserRecord as settled for multi-record users.
      const preferred = preferredUserPath
        ? (userRecords.find((r) => r.path === preferredUserPath) ?? null)
        : null;
      userRecord = preferred ?? userRecords[0];
    }

    // Get user group from localStorage like the login page does
    const storedUserGroups = localStorage.getItem("userGroups");
    const userGroups = storedUserGroups ? JSON.parse(storedUserGroups) : [];
    const userGroup =
      userGroups.length > 0 ? userGroups[0].toLowerCase() : "unknown";

    const uniqueIdentifiers: UniqueIdentifier[] = userRecords.map((record) => {
      const pathParts = record.path?.split("#") || [];

      const districtID =
        pathParts.find((part) => part.startsWith("dis-")) || null;
      const schoolID =
        pathParts.find((part) => part.startsWith("sch-")) || null;
      const sectionID =
        pathParts.find((part) => part.startsWith("sec-")) || null;
      return {
        userPath: record.path || "",
        schoolID,
        sectionID,
        districtID,
      };
    });

    // Compute role-based access properties
    //
    // Two roles have no district of their own — a superadmin is parented to a
    // supervisory union, Hats staff to an organization — so both pick one via
    // setActiveDistrict and then act as a district admin for it. They share one
    // flag rather than being special-cased separately at every call site.
    const hasEnteredADistrict =
      (userGroup === "superadmin" || userGroup === "hatsstaff") &&
      !!userRecord.activeDistrictId;

    // Hats staff can only read unless they have the helpDesk flag. They still
    // enter districts for scoping, but never get the district admin view.
    const isReadOnlyStaff = userGroup === "hatsstaff" && !userRecord.helpDesk;
    const actsAsDistrictAdmin = hasEnteredADistrict && !isReadOnlyStaff;

    // District ID: the entered district for those roles, otherwise from the path
    const districtId = hasEnteredADistrict
      ? userRecord.activeDistrictId!
      : userRecord.path?.match(/dis-[^#]+/)?.[0] || undefined;

    // Effective role: having entered a district, they act as a district admin
    const effectiveRole = actsAsDistrictAdmin ? "districtadmin" : userGroup;

    const isDistrictAdmin = userGroup === "districtadmin";

    // Is educator role: teachers, school staff, district admins, or a
    // district-less role that has entered a district
    const isEducatorRole =
      userGroup === "teacher" ||
      userGroup === "schoolstaff" ||
      userGroup === "districtadmin" ||
      actsAsDistrictAdmin;

    const isHelpDesk =
      !!userRecord.helpDesk &&
      (userGroup === "districtadmin" || userGroup === "hatsstaff");

    return {
      ...userRecord,
      userID: userRecord.id,
      role: userGroup,
      schoolLevel: null, // TODO - find where we use this and use feature flags instead
      userPath: userRecord.path || "",
      uniqueIdentifiers,
      // Computed properties
      districtId,
      effectiveRole,
      isDistrictAdmin,
      isEducatorRole,
      isHelpDesk,
      isReadOnlyStaff,
      hasEnteredADistrict,
    };
  }, [userRecords, userRecordsLoading, preferredUserPath]);

  return {
    userRecords,
    userRecordsLoading,
    userRecordsError,
    activeUserRecord,
  };
};

export const updateUserRecordCache = (
  queryClient: QueryClient,
  updateFunction: (record: FullUserRecord) => FullUserRecord,
): FullUserRecord[] => {
  const userRecords = queryClient.getQueryData<FullUserRecord[]>([
    "myUserRecords",
  ]);
  if (!userRecords) return [];

  const updatedUserRecords = userRecords.map(updateFunction);
  queryClient.setQueryData(["myUserRecords"], updatedUserRecords);
  return updatedUserRecords;
};

// Returns the one active user record
export const useCurrentUser = (): ExtendedUser | Record<string, never> => {
  const { activeUserRecord } = useFullCurrentUser();
  return activeUserRecord || ({} as const);
};
